import { Form, FormInstance, notification, Select } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/store';
import { uiModel } from 'entities/ui';
import { SubstitutionsTypes } from 'shared/enums';
import {
  useCreateSubstitutionMutation,
  useLazyGetAllDivisionsForAddSubstitutionQuery,
} from 'app/services/edpApiV1/substitutions/substitution';
import { useEffect, useState } from 'react';
import { DatePeriodField } from 'shared/ui/form/DatePeriodField';
import { viewerModel } from 'entities/viewer';
import { getErrorData } from 'shared/helpers/utils';
import { convertDate } from 'shared/helpers/date';
import { SERVER_DATE_MASK } from 'shared/constants';
import { useDivisionsQuery } from 'app/services/edpApiV1/viewer';
import { AllDivisionsForSubstitution } from 'app/services/edpApiV1/substitutions/types';
import { returnErrorOrMessage } from 'shared/helpers/errors';
import { SubstitutionFormData } from '../../types';
import { SearchEmployeesField } from '../search-employees-field';
import style from '../index.module.scss';
import { EmployeeVocations } from '../employee-vocations';

interface IProps {
  form: FormInstance<SubstitutionFormData>;
  setLoading?: (isLoading: boolean) => void;
}

export const AddSubstitution: React.FC<IProps> = ({ form, setLoading }) => {
  const dispatch = useAppDispatch();

  const { name, middleName, lastName, position } = useAppSelector(viewerModel.me.selectors.getViewerProfileInfo);
  const { data: divisions, isLoading: isGetDivisionsLoading } = useDivisionsQuery(null);

  const [getAllDivisionsData, { isLoading: isGetAlldivisionLoading }] = useLazyGetAllDivisionsForAddSubstitutionQuery();
  const [createSubstitutionTrigger, { isSuccess: createIsSucces, isLoading: isCreateLoading }] =
    useCreateSubstitutionMutation({
      selectFromResult: result => ({ ...result, error: getErrorData(result.error) }),
    });

  const [divisionId, setDivisionId] = useState<string | undefined>();
  const [substitutionType, setSubstitutionType] = useState<SubstitutionsTypes>(SubstitutionsTypes.deputy);

  useEffect(() => {
    setLoading?.(isCreateLoading || isGetAlldivisionLoading);
  }, [isCreateLoading, isGetAlldivisionLoading]);

  const checkSubstitutionType = async () => {
    if (!divisionId) {
      return;
    }
    const size = 100;
    let currentPage = 1;
    let allDivisionsData = await getAllDivisionsData({ size, page: currentPage }).unwrap();
    const lastPage = Math.ceil(allDivisionsData.pagination.total / allDivisionsData.pagination.size);
    let divisionWithSupervisor: AllDivisionsForSubstitution | undefined;

    while (currentPage <= lastPage) {
      divisionWithSupervisor = allDivisionsData.data.find(elem => elem.id === divisionId);
      if (divisionWithSupervisor?.supervisor) {
        setSubstitutionType(SubstitutionsTypes.deputy);
        break;
      }
      currentPage++;
      allDivisionsData = await getAllDivisionsData({ size, page: currentPage }).unwrap();
    }

    if (!divisionWithSupervisor?.supervisor) {
      setSubstitutionType(SubstitutionsTypes.acting);
    }
  };

  useEffect(() => {
    form.resetFields(['employeeId']);
    if (divisionId) {
      checkSubstitutionType();
    }
  }, [divisionId]);

  const onFinish = async (data: SubstitutionFormData) => {
    try {
      const { employeeId, period, divisionId } = data;

      const newSubstitution = {
        type: substitutionType,
        divisionId,
        employeeId,
        validFrom: convertDate(period[0], { format: SERVER_DATE_MASK }),
        validTo: convertDate(period[1], { format: SERVER_DATE_MASK }),
      };

      const result = await createSubstitutionTrigger(newSubstitution);
      //React не отловил ошибку, отловлю я
      if ((result as any)?.error) {
        throw new Error((result as any)?.error?.data?.message || 'Что-то пошло не так');
      }

      notification.success({
        message: substitutionType === SubstitutionsTypes.acting ? 'И.О. назначен' : 'Замещающий назначен',
      });
    } catch (e: any) {
      notification.error({ message: returnErrorOrMessage(e, 'Что-то пошло не так') });
    }
  };

  useEffect(() => {
    if (createIsSucces) {
      dispatch(uiModel.Substitutions.actions.closeSubstitutionControlDrawer());
    }
  }, [createIsSucces]);

  return (
    <>
      <div className={style.author}>
        <h4>Автор замещения:</h4>
        <div>{`${lastName} ${name ? name[0] : ''}. ${middleName ? middleName[0] : ''}.`}</div>
        <div className={style.position}>{position || ''}</div>
      </div>

      <Form layout="vertical" form={form} onFinish={onFinish}>
        <Form.Item
          name={'divisionId'}
          label={'Выберите подразделение'}
          required
          rules={[
            {
              required: true,
              message: 'Выберите подразделение',
            },
          ]}
        >
          <Select
            options={divisions?.data.map(({ id, name }) => ({ label: name, value: id }))}
            placeholder={'Выберите подразделение'}
            loading={isGetDivisionsLoading}
            onChange={val => setDivisionId(val)}
          ></Select>
        </Form.Item>
        <SearchEmployeesField
          divisionId={divisionId}
          searchDivisionId={substitutionType === SubstitutionsTypes.acting ? undefined : divisionId}
        />
        <EmployeeVocations />
        <DatePeriodField />
      </Form>
    </>
  );
};
